import React, { useEffect, useState } from 'react';
import { Menu, Modal } from 'antd';
import { ConnectionModel } from '../../connection.model';
import QuickStartComponent from './quick-start-component';
import SmartPolicyComponent from './smart-policy-component';
import PullFromExistingComponent from './pull-from-existing-component';

import QuickStartIcon from '@svg/protection.svg?react';
import SmartPolicyIcon from '@svg/smart-indication.svg?react';
import PullFromExistingIcon from '@svg/pull-policy-icon.svg?react';

import './policy-actions-modal.scss';
import { formatConnectionCount, isAllIneligibleSmart, isAllNonInitialConnections } from 'src/utils/connection-utils';
import { observer } from 'mobx-react-lite';
import { useStores } from 'src/components/store';
import { EventType, policyActionsLocationMap } from 'src/dtos/analytics.dto';
import {ECMActionType} from 'src/utils/enums';

const actionMap = {
    [ECMActionType.QuickStart]: {
        icon: QuickStartIcon,
        title: 'Quick Start Policy',
        toastTitle: () => 'Your Policy is being applied',
        toastDesc: (count: number) =>
            `${formatConnectionCount(count, true)} now being applied. Once completed, you will be notified by email.`
    },
    [ECMActionType.SmartPolicy]: {
        icon: SmartPolicyIcon,
        title: 'Smart Policy',
        toastTitle: (_toApply: boolean, soakDays?: number) =>
            soakDays ? 'We have started analyzing your network traffic.' : 'We have started building your smart policy.',
        toastDesc: (_count: number, _toApply: boolean, soakDays?: number) =>
            soakDays
                ? `Analysis needs ${soakDays} days of service. You will be notified by email once completed.`
                : 'You will be notified by email once ready.'
    },
    [ECMActionType.PullFromExisting]: {
        icon: PullFromExistingIcon,
        title: 'Assign from existing policy',
        toastTitle: (toApply: boolean) =>
            toApply ? 'Your Policy is being applied' : 'Your draft policy is being created',
        toastDesc: (count: number, toApply: boolean) =>
            toApply
                ? `${formatConnectionCount(count, true)} now being applied. Once completed, you will be notified by email.`
                : `Draft policy is successfully created for ${formatConnectionCount(count)}.`
    }
};

interface PolicyActionsModalProps {
    visible: boolean;
    onClose: () => void;
    initialAction?: ECMActionType;
    selectedConnections: Map<number, ConnectionModel>;
    setSelectedConnections?: React.Dispatch<React.SetStateAction<Map<number, ConnectionModel>>>;
}

const PolicyActionsModal: React.FC<PolicyActionsModalProps> = observer(({ visible, onClose, initialAction, selectedConnections, setSelectedConnections }) => {
    const [selectedAction, setSelectedAction] = useState<ECMActionType>(initialAction || ECMActionType.QuickStart);

    const { customerStore } = useStores();

    // Reset the selected action whenever the modal visibility, initialAction, or selected connections change.
    // Defaults to SmartPolicy (instead of QuickStart) when all connections are non-initial, since QuickStart is disabled.
    useEffect(() => {
        let defaultAction = ECMActionType.QuickStart;
        if (isAllNonInitialConnections(selectedConnections)) {
            if (isAllIneligibleSmart(selectedConnections)) {
                defaultAction = ECMActionType.PullFromExisting;
            } else {
                defaultAction = ECMActionType.SmartPolicy;
            }
        }

        setSelectedAction(initialAction ?? defaultAction);
    }, [visible, initialAction, selectedConnections]);

    const onCancel = () => {
        customerStore.sendGeneralEvent(
            EventType.LinkClicked,
            {
                button: 'cancel',
                number_of_sid: selectedConnections.size,
                location: policyActionsLocationMap[selectedAction],
            }
        );
        onClose();
    };

    const renderContent = (menu: ECMActionType) => {
        switch (menu) {
            case ECMActionType.QuickStart:
                return <QuickStartComponent selectedConnections={selectedConnections}
                                            setSelectedConnections={setSelectedConnections}
                                            showToast={showToast} />;
            case ECMActionType.SmartPolicy:
                return <SmartPolicyComponent selectedConnections={selectedConnections}
                                             setSelectedConnections={setSelectedConnections}
                                             showToast={showToast} />;
            case ECMActionType.PullFromExisting:
                return <PullFromExistingComponent selectedConnections={selectedConnections}
                                                  setSelectedConnections={setSelectedConnections}
                                                  showToast={showToast} />;
        }
    };

    const getHeaderTitle = () => {
        const currentAction = initialAction ?? selectedAction;
        return initialAction ? actionMap[currentAction].title : 'New Policy';
    };

    // QuickStart is disabled when all selected connections are non-initial (version > 1 with active policy)
    const isQuickStartHidden = isAllNonInitialConnections(selectedConnections);
    const isSmartProtectionHidden = isAllIneligibleSmart(selectedConnections);

    const getConnectionInfo = () => {
        if (selectedConnections.size === 1) {
            const connection = Array.from(selectedConnections.values())[0];
            return `Connection: ${connection.connectionName || connection.circuitId}`;
        }
        return `${selectedConnections.size} connections selected`;
    };

    const showToast = (actionType: ECMActionType, count: number, toApply = true, soakDays?: number) => {
        if (count === 0) {
            customerStore.setToastData({ title: 'Error',
                desc: 'We are having a problem. Please try again or contact your administrator.' });
        } else {
            const { toastTitle, toastDesc } = actionMap[actionType];
            customerStore.setToastData({ title: toastTitle(toApply, soakDays), desc: toastDesc(count, toApply, soakDays) });
        }
        onClose();
    };

    return (
        <Modal open={visible} onCancel={onCancel} footer={null} className="policy-actions-modal"
               width={initialAction ? 820 : 1140}>
            <div className="policy-actions-header">
                <div className="policy-actions-header-title">{getHeaderTitle()}</div>
                <div className="policy-actions-header-info">{getConnectionInfo()}</div>
            </div>
            <div className="policy-action-modal-content">
                {/* Always render menu with all three options; QuickStart disabled for all-non-initial selections */}
                {!(isQuickStartHidden && isSmartProtectionHidden) && !initialAction && <Menu onClick={(e) => setSelectedAction(e.key as ECMActionType)} mode="vertical"
                      selectedKeys={[selectedAction]} className="policy-actions-modal-menu">
                    {Object.values(ECMActionType).map(item => {
                        const IconComponent = actionMap[item].icon;
                        const isItemHidden = (item === ECMActionType.QuickStart && isQuickStartHidden) ||
                            (item === ECMActionType.SmartPolicy && isSmartProtectionHidden);
                        return (
                            isItemHidden ? null : <Menu.Item key={item} className="policy-actions-modal-menu-item">
                                <IconComponent className="menu-item-icon"/>
                                {item}
                            </Menu.Item>
                        );
                    })}
                </Menu>}
                <div className="policy-action-modal-component">{renderContent(selectedAction)}</div>
            </div>
        </Modal>
    );
});

export default PolicyActionsModal;
